import * as React from "react"
import { router, useForm } from "@inertiajs/react"
import { AlertTriangle, CheckCircle2, Play, RotateCw, Save } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { chicagoDateTimeLocalToIso, formatChicagoDateTime, toChicagoDateTimeLocalInputValue } from "@/lib/chicagoTime"

type DelayUnit = "hours" | "business_days" | "calendar_days"

type Settings = {
  new_lead_transition_enabled: boolean
  new_lead_delay_value: number
  new_lead_delay_unit: DelayUnit
  age_based_lost_enabled: boolean
  age_based_lost_delay_value: number
  age_based_lost_delay_unit: DelayUnit
  fieldroutes_won_sync_enabled: boolean
  fieldroutes_lost_sync_enabled: boolean
  follow_up_maintenance_enabled: boolean
  execution_enabled: boolean
  go_live_at: string
  config_version: number
  updated_at: string | null
  updated_by_email: string | null
}

type Run = {
  id: number
  mode: string
  mode_label: string
  source: string
  source_label: string
  status: string
  execution_requested: boolean
  execution_permitted: boolean
  settings_version: number
  started_at: string | null
  finished_at: string | null
  duration_ms: number | null
  candidates_checked: number
  changed_count: number
  already_aligned_count: number
  review_required_count: number
  error_count: number
  follow_up_actions_count: number
  error_summary: string | null
  initiated_by_email: string | null
} | null

type Result = {
  id: number
  lead_id: number | null
  lead_name: string
  current_stage_key: string | null
  current_stage_label: string | null
  target_stage_key: string | null
  target_stage_label: string | null
  action_label: string
  result_label: string
  decision: string
  category: string
  review_required: boolean
  execution_eligible: boolean
  execution_block_reason: string | null
  executed: boolean
  stage_changed: boolean
  follow_up_action: string | null
  fieldroutes_state_source: string | null
  reason: string | null
  error_message: string | null
  processed_at: string | null
}

type Props = {
  settings: Settings
  server_execution_enabled: boolean
  last_run: Run
  last_successful_run: Run
  recent_runs: Run[]
  recent_results: Result[]
}

const unitOptions = [
  { value: "hours", label: "Hours" },
  { value: "business_days", label: "Business days" },
  { value: "calendar_days", label: "Calendar days" },
]

function formatDate(value?: string | null) {
  return formatChicagoDateTime(value)
}

function StatusPill({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return <span className={ok ? "inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-300" : "inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300"}>{ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}{children}</span>
}

function SettingToggle({ label, checked, onChange, description }: { label: string; checked: boolean; onChange: (value: boolean) => void; description?: string }) {
  return (
    <label className="flex items-start gap-3 rounded-md border border-hairline bg-surface/40 p-3">
      <Checkbox checked={checked} onChange={(event) => onChange(event.currentTarget.checked)} />
      <span className="grid gap-1">
        <span className="text-sm font-medium text-ink-display">{label}</span>
        {description && <span className="text-xs text-ink-muted">{description}</span>}
      </span>
    </label>
  )
}

function DelayControls({ value, unit, onValueChange, onUnitChange }: { value: number; unit: DelayUnit; onValueChange: (value: number) => void; onUnitChange: (value: DelayUnit) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-ink-muted">After</span>
      <Input type="number" min="0" max="365" value={value} onChange={(event) => onValueChange(Number(event.target.value))} className="w-24" />
      <Select value={unit} onChange={(event) => onUnitChange(event.target.value as DelayUnit)} className="w-44">
        {unitOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
    </div>
  )
}

export default function LifecycleAutomation({ settings, server_execution_enabled, last_run, last_successful_run, recent_runs, recent_results }: Props) {
  const form = useForm({ commercial_lifecycle_setting: { ...settings } })
  const setting = form.data.commercial_lifecycle_setting
  const canRun = server_execution_enabled && setting.execution_enabled

  function updateSetting<K extends keyof Settings>(key: K, value: Settings[K]) {
    form.setData("commercial_lifecycle_setting", { ...setting, [key]: value })
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    form.patch("/admin/lifecycle-automation", { preserveScroll: true })
  }

  return (
    <AppShell wide>
      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-6 lg:px-8">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-semibold text-ink-display">Lifecycle Automation</h1>
            <p className="mt-1 max-w-2xl text-sm text-ink-muted">Lifecycle timing is configured here. Google Apps Script synchronizes lifecycle state with FieldRoutes and submits verified results to Commercial LeadFlow.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <StatusPill ok={server_execution_enabled}>Server gate {server_execution_enabled ? "enabled" : "disabled"}</StatusPill>
            <StatusPill ok={setting.execution_enabled}>Admin execution {setting.execution_enabled ? "enabled" : "disabled"}</StatusPill>
          </div>
        </header>

        <form onSubmit={submit} className="grid gap-5 rounded-lg border border-hairline bg-page p-5 shadow-sm">
          <section className="grid gap-3">
            <h2 className="text-sm font-semibold text-ink-display">New Lead Automation</h2>
            <SettingToggle label="Automatically move New Lead to Needs Follow-Up" checked={setting.new_lead_transition_enabled} onChange={(value) => updateSetting("new_lead_transition_enabled", value)} />
            <DelayControls value={setting.new_lead_delay_value} unit={setting.new_lead_delay_unit} onValueChange={(value) => updateSetting("new_lead_delay_value", value)} onUnitChange={(value) => updateSetting("new_lead_delay_unit", value)} />
          </section>

          <section className="grid gap-3 border-t border-hairline pt-5">
            <h2 className="text-sm font-semibold text-ink-display">Age-Based Lost</h2>
            <SettingToggle label="Automatically mark aging Needs Follow-Up leads Lost" checked={setting.age_based_lost_enabled} onChange={(value) => updateSetting("age_based_lost_enabled", value)} description="Disabled by default so commercial opportunities do not become Lost just because they age." />
            <DelayControls value={setting.age_based_lost_delay_value} unit={setting.age_based_lost_delay_unit} onValueChange={(value) => updateSetting("age_based_lost_delay_value", value)} onUnitChange={(value) => updateSetting("age_based_lost_delay_unit", value)} />
          </section>

          <section className="grid gap-3 border-t border-hairline pt-5">
            <h2 className="text-sm font-semibold text-ink-display">FieldRoutes Synchronization</h2>
            <div className="grid gap-3 md:grid-cols-2">
              <SettingToggle label="Move Commercial lead to Won when FieldRoutes safely confirms Won" checked={setting.fieldroutes_won_sync_enabled} onChange={(value) => updateSetting("fieldroutes_won_sync_enabled", value)} />
              <SettingToggle label="Move Commercial lead to Lost when FieldRoutes explicitly confirms Lost" checked={setting.fieldroutes_lost_sync_enabled} onChange={(value) => updateSetting("fieldroutes_lost_sync_enabled", value)} />
              <SettingToggle label="Maintain routine commercial follow-ups automatically" checked={setting.follow_up_maintenance_enabled} onChange={(value) => updateSetting("follow_up_maintenance_enabled", value)} />
              <SettingToggle label="Enable automatic lifecycle changes" checked={setting.execution_enabled} onChange={(value) => updateSetting("execution_enabled", value)} description="Writes still require the server emergency gate." />
            </div>
          </section>

          <section className="grid gap-3 border-t border-hairline pt-5">
            <h2 className="text-sm font-semibold text-ink-display">Rollout</h2>
            <label className="grid gap-1 text-sm">
              <span className="text-ink-muted">Execution go-live timestamp</span>
              <Input type="datetime-local" value={toChicagoDateTimeLocalInputValue(setting.go_live_at)} onChange={(event) => updateSetting("go_live_at", chicagoDateTimeLocalToIso(event.target.value) as Settings["go_live_at"])} className="max-w-xs" />
            </label>
            <p className="text-xs text-ink-muted">Preview includes older leads. Automatic execution applies only to leads created at or after this timestamp.</p>
          </section>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline pt-5">
            <p className="text-xs text-ink-muted">Version {setting.config_version}. Last updated {formatDate(setting.updated_at)}{setting.updated_by_email ? ` by ${setting.updated_by_email}` : ""}.</p>
            <Button type="submit" disabled={form.processing}><Save className="h-4 w-4" /> Save settings</Button>
          </div>
        </form>

        <section className="grid gap-4 rounded-lg border border-hairline bg-page p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-ink-display">Manual Run</h2>
              <p className="text-sm text-ink-muted">Google Apps Script is the production scheduler. Manual runs remain diagnostic; Commercial changes still require both gates.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" onClick={() => router.post("/admin/lifecycle-automation/preview", {}, { preserveScroll: true })}><RotateCw className="h-4 w-4" /> Run Preview Now</Button>
              <Button type="button" disabled={!canRun} onClick={() => router.post("/admin/lifecycle-automation/run", {}, { preserveScroll: true })}><Play className="h-4 w-4" /> Run Reconciliation Now</Button>
            </div>
          </div>
          <div className="grid gap-3 text-sm md:grid-cols-2">
            <RunSummary title="Last run" run={last_run} />
            <RunSummary title="Last successful run" run={last_successful_run} />
          </div>
        </section>

<section className="grid gap-4 rounded-lg border border-hairline bg-page p-5 shadow-sm">
  <h2 className="text-sm font-semibold text-ink-display">Recent Runs</h2>
  <div className="overflow-x-auto">
    <table className="min-w-full text-left text-sm">
      <thead className="text-xs uppercase text-ink-muted"><tr><th className="py-2 pr-4">Started</th><th className="py-2 pr-4">Mode</th><th className="py-2 pr-4">Source</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Checked</th><th className="py-2 pr-4">Changed</th><th className="py-2 pr-4">Review</th><th className="py-2 pr-4">Errors</th></tr></thead>
      <tbody className="divide-y divide-hairline">
        {recent_runs.length === 0 && <tr><td colSpan={8} className="py-4 text-ink-muted">No lifecycle runs yet.</td></tr>}
        {recent_runs.map((run) => run && (
          <tr key={run.id}>
            <td className="py-2 pr-4 text-ink-muted">{formatDate(run.started_at)}</td>
            <td className="py-2 pr-4 text-ink-body">{run.mode_label}</td>
            <td className="py-2 pr-4 text-ink-body">{run.source_label}</td>
            <td className="py-2 pr-4 text-ink-body">{run.status}</td>
            <td className="py-2 pr-4 text-ink-body">{run.candidates_checked}</td>
            <td className="py-2 pr-4 text-ink-body">{run.changed_count}</td>
            <td className="py-2 pr-4 text-ink-body">{run.review_required_count}</td>
            <td className="py-2 pr-4 text-ink-body">{run.error_count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</section>

        <section className="grid gap-4 rounded-lg border border-hairline bg-page p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-ink-display">Recent Activity</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase text-ink-muted"><tr><th className="py-2 pr-4">Time</th><th className="py-2 pr-4">Lead</th><th className="py-2 pr-4">Current</th><th className="py-2 pr-4">Action</th><th className="py-2 pr-4">Result</th><th className="py-2 pr-4">Execution</th><th className="py-2 pr-4">Reason</th></tr></thead>
              <tbody className="divide-y divide-hairline">
                {recent_results.length === 0 && <tr><td colSpan={7} className="py-4 text-ink-muted">No lifecycle results yet.</td></tr>}
                {recent_results.map((result) => (
                  <tr key={result.id}>
                    <td className="py-2 pr-4 text-ink-muted">{formatDate(result.processed_at)}</td>
                    <td className="py-2 pr-4 text-ink-display">{result.lead_name}</td>
                    <td className="py-2 pr-4 text-ink-body">{result.current_stage_label || "-"}</td>
                    <td className="py-2 pr-4 text-ink-body">{result.action_label}</td>
                    <td className="py-2 pr-4 text-ink-body">{result.result_label}</td>
                    <td className="py-2 pr-4 text-ink-muted">{result.execution_block_reason || (result.execution_eligible ? "Eligible" : "-")}</td>
                    <td className="py-2 pr-4 text-ink-muted">{result.error_message || result.reason || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </AppShell>
  )
}

function RunSummary({ title, run }: { title: string; run: Run }) {
  if (!run) return <div className="rounded-md bg-surface/50 p-3 text-sm text-ink-muted">{title}: Not yet</div>
  return (
    <div className="rounded-md bg-surface/50 p-3">
      <div className="mb-2 flex items-center justify-between gap-2"><span className="font-medium text-ink-display">{title}</span><span className="text-xs text-ink-muted">{formatDate(run.started_at)}</span></div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm"><dt className="text-ink-muted">Mode</dt><dd className="text-ink-body">{run.mode_label}</dd><dt className="text-ink-muted">Source</dt><dd className="text-ink-body">{run.source_label}</dd><dt className="text-ink-muted">Status</dt><dd className="text-ink-body">{run.status}</dd><dt className="text-ink-muted">Checked</dt><dd className="text-ink-body">{run.candidates_checked}</dd><dt className="text-ink-muted">Changed</dt><dd className="text-ink-body">{run.changed_count}</dd><dt className="text-ink-muted">Review</dt><dd className="text-ink-body">{run.review_required_count}</dd><dt className="text-ink-muted">Errors</dt><dd className="text-ink-body">{run.error_count}</dd></dl>
    </div>
  )
}
